/**
 * Cookie service for managing browser cookies
 */
export class CookieService {
  /**
   * Set a cookie with optional expiration
   * @param name Cookie name
   * @param value Cookie value
   * @param days Days until expiration (default: 7 days)
   * @param path Cookie path (default: '/')
   * @param secure Use secure cookie (default: true for HTTPS)
   * @param sameSite SameSite attribute (default: 'strict')
   */
  static setCookie(
    name: string, 
    value: string, 
    days: number = 7,
    path: string = '/',
    secure: boolean = window.location.protocol === 'https:',
    sameSite: 'strict' | 'lax' | 'none' = 'strict'
  ): void {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    
    let cookieString = `${name}=${encodeURIComponent(value)}`;
    cookieString += `; expires=${expires.toUTCString()}`;
    cookieString += `; path=${path}`;
    
    if (secure) {
      cookieString += '; secure';
    }
    
    cookieString += `; samesite=${sameSite}`;
    
    document.cookie = cookieString;
  }

  /**
   * Get a cookie value by name
   * @param name Cookie name
   * @returns Cookie value or null if not found
   */
  static getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');
    
    for (let cookie of cookies) {
      let c = cookie.trim();
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length));
      }
    }
    
    return null;
  }

  /**
   * Delete a cookie by name
   * @param name Cookie name
   * @param path Cookie path (default: '/')
   */
  static deleteCookie(name: string, path: string = '/'): void {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
  }

  /**
   * Check if a cookie exists
   * @param name Cookie name
   * @returns True if cookie exists
   */
  static hasCookie(name: string): boolean {
    return this.getCookie(name) !== null;
  }

  /**
   * Get all cookies as an object
   * @returns Object with cookie names as keys and values as values
   */
  static getAllCookies(): Record<string, string> {
    const cookies: Record<string, string> = {};
    const cookieStr = document.cookie;
    
    if (cookieStr) {
      cookieStr.split(';').forEach(cookie => {
        const [name, value] = cookie.trim().split('=');
        if (name && value) {
          cookies[name] = decodeURIComponent(value);
        }
      });
    }
    
    return cookies;
  }

  /**
   * Clear all cookies (only ones accessible from current path)
   */
  static clearAllCookies(): void {
    const cookies = this.getAllCookies();
    Object.keys(cookies).forEach(name => {
      this.deleteCookie(name);
    });
  }
}